import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import CancelIcon from '@/assets/cancel3.svg?react';
import NavArrowDownIcon from '@/assets/nav-arrow-down.svg?react';
import NavArrowUpIcon from '@/assets/nav-arrow-up.svg?react';
import { FormattedMessage } from 'react-intl';
import { MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';

const SELECTION_HEIGHT = 24;

const SelectHead = <
  T extends string[] | number[] | string | number | null,
>({
  open,
  onClick,
  variant,
  multiple,
  selectedOptions,
  placeholder,
  disabled = false,
  showClear = false,
  onClear,
  'aria-label': ariaLabel,
}: {
  open: boolean;
  onClick: () => void;
  variant: 'text' | 'outlined';
  multiple: boolean;
  selectedOptions: {
    id: number | string;
    value: T;
    label: ReactNode;
    icon?: ReactNode;
  }[];
  placeholder?: React.ReactNode;
  disabled?: boolean;
  showClear?: boolean;
  onClear?: (e: MouseEvent<HTMLButtonElement>) => void;
  'aria-label': string;
}) => {
  const selectionsRef = useRef<HTMLDivElement>(null);
  const [numFirstRow, setNumFirstRow] = useState(0);
  const moreCount = selectedOptions.length - numFirstRow;

  useEffect(() => {
    if (selectionsRef.current) {
      const container = selectionsRef.current;
      let basePosition: number | null = null;
      let newCount = 0;

      for (let i = 0; i < container.children.length; i++) {
        const child = container.children[i] as HTMLElement;
        const clientRect = child.getBoundingClientRect();
        if (basePosition === null) {
          basePosition = clientRect.y;
        }
        if (clientRect.y > basePosition) {
          break;
        } else {
          newCount++;
        }
      }
      setNumFirstRow(newCount);
    }
  }, [selectedOptions]);

  return (
    <Root
      onClick={disabled ? undefined : onClick}
      role="button"
      aria-label={ariaLabel}
      aria-expanded={open}
      aria-disabled={disabled}
      disabled={disabled}
    >
      <SelectionsContainer ref={selectionsRef}>
        {selectedOptions.length
          ? selectedOptions.map((o) => (
              <Selection key={o.id}>
                {o?.icon}
                <Label variant={variant} disabled={disabled}>
                  {o?.label}
                </Label>
              </Selection>
            ))
          : placeholder}
      </SelectionsContainer>
      {multiple && moreCount > 0 && (
        <Label variant={variant} disabled={disabled}>
          <FormattedMessage
            id="more"
            defaultMessage={'{number} more...'}
            values={{ number: moreCount }}
          />
        </Label>
      )}
      {showClear && onClear && (
        <ClearButton
          size="small"
          aria-label="Clear selection"
          onClick={(e) => {
            e.stopPropagation();
            onClear(e);
          }}
        >
          <CancelIcon />
        </ClearButton>
      )}
      {variant === 'outlined' ? (
        <StyledNavArrowDownIcon open={open} disabled={disabled} />
      ) : (
        <StyledNavArrowUpIcon open={open} disabled={disabled} />
      )}
    </Root>
  );
};

const Root = styled('div')<{ disabled?: boolean }>(
  ({ theme, disabled = false }) => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    padding: '12px',
    userSelect: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
  }),
);

const SelectionsContainer = styled('div')(() => ({
  flexGrow: 1,
  overflow: 'hidden',
  height: SELECTION_HEIGHT,
  display: 'flex',
  flexWrap: 'wrap',
}));

const Selection = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'nowrap',
  marginRight: theme.spacing(1),
}));

const ClearButton = styled(IconButton)(({ theme }) => ({
  flexShrink: 0,
  padding: theme.spacing(0.5),
  color: theme.palette.grey500,
  path: {
    stroke: theme.palette.grey500,
  },
}));

const StyledNavArrowDownIcon = styled(NavArrowDownIcon)<{
  open: boolean;
  disabled?: boolean;
}>(({ theme, open, disabled = false }) => ({
  flexShrink: 0,
  path: {
    transition: '0.2s all ease-in-out',
    stroke: disabled
      ? theme.palette.text?.disabled || '#00000042'
      : open
        ? theme.palette.textPrimaryBlack
        : theme.palette.iconGrey,
    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
    transformOrigin: 'center',
  },
}));

const StyledNavArrowUpIcon = styled(NavArrowUpIcon)<{
  open: boolean;
  disabled?: boolean;
}>(({ theme, open, disabled = false }) => ({
  flexShrink: 0,
  path: {
    transition: '0.2s all ease-in-out',
    stroke: disabled
      ? theme.palette.text?.disabled || '#00000042'
      : undefined,
    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
    transformOrigin: 'center',
  },
}));

const Label = styled('div')<{
  variant: 'text' | 'outlined';
  disabled?: boolean;
}>(({ theme, variant, disabled = false }) => ({
  flexShrink: 0,
  flexGrow: 1,
  fontSize: variant === 'outlined' ? 16 : 14,
  lineHeight: '24px',
  color: disabled
    ? theme.palette.text?.disabled || '#00000042'
    : undefined,
}));

export default SelectHead;
